import {createUsePuck} from "@puckeditor/core";
import {useEffect, useRef, useState} from "react";
import {useSafePuckFieldOnChange} from "./fields";
import {normalizeStyle, writeStyle} from "./serialization";
import type {BlockStyle} from "./types";

const useBuilderPuck = createUsePuck();

const colorKeys = new Set<keyof BlockStyle>(["textColor", "backgroundColor"]);

const StyleFields = ({ value, onChange }: { value?: BlockStyle | string; onChange: (style?: string) => void }) => {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(() => normalizeStyle(value));
  const incomingKey = JSON.stringify(value ?? {});
  const incomingKeyRef = useRef(incomingKey);
  const persistTimeout = useRef<number>();
  const pendingStyle = useRef(draft);
  const rootRef = useRef<HTMLElement>(null);
  const commit = useSafePuckFieldOnChange(onChange);

  useEffect(() => {
    if (incomingKeyRef.current === incomingKey) return;
    incomingKeyRef.current = incomingKey;
    pendingStyle.current = normalizeStyle(value);
    setDraft(pendingStyle.current);
  }, [incomingKey, value]);
  useEffect(() => {
    const flush = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      if (persistTimeout.current === undefined) return;
      window.clearTimeout(persistTimeout.current);
      persistTimeout.current = undefined;
      commit(writeStyle(pendingStyle.current));
    };
    document.addEventListener("pointerdown", flush, true);
    return () => {
      document.removeEventListener("pointerdown", flush, true);
      window.clearTimeout(persistTimeout.current);
    };
  }, [commit]);
  const update = <K extends keyof BlockStyle>(key: K, next: BlockStyle[K] | undefined) => {
    const style = { ...(draft ?? {}) };
    if (next === undefined) delete style[key];
    else style[key] = next;
    const resolved = Object.keys(style).length ? style : undefined;
    pendingStyle.current = resolved;
    setDraft(resolved);
    const stored = writeStyle(resolved);
    if (!colorKeys.has(key)) {
      window.clearTimeout(persistTimeout.current);
      commit(stored);
      return;
    }
    // Native color inputs fire continuously while the picker is dragged. Keep
    // the swatch on local draft and debounce Puck so the editor does not walk
    // the page slot on every pointer move.
    window.clearTimeout(persistTimeout.current);
    persistTimeout.current = window.setTimeout(() => commit(stored), 160);
  };

  return <section className="template-builder-style" ref={rootRef}>
    <button type="button" onClick={() => setExpanded((current) => !current)} aria-expanded={expanded}>Style configuration</button>
    {expanded && <div>
      <label>Font family<select value={draft?.fontFamily ?? ""} onChange={(event) => update("fontFamily", (event.target.value || undefined) as BlockStyle["fontFamily"])}><option value="">Document default</option>{["Courier", "Helvetica", "Times-Roman", "Symbol", "ZapfDingbats"].map((item) => <option key={item}>{item}</option>)}</select></label>
      <label>Font size<input type="number" min="6" max="72" value={draft?.fontSize ?? ""} onChange={(event) => update("fontSize", Number.isFinite(event.target.valueAsNumber) ? Math.min(72, Math.max(6, event.target.valueAsNumber)) : undefined)} /></label>
      <label>Weight<select value={draft?.fontWeight ?? ""} onChange={(event) => update("fontWeight", (event.target.value || undefined) as BlockStyle["fontWeight"])}><option value="">Document default</option><option value="normal">Normal</option><option value="bold">Bold</option></select></label>
      <label>Italic<input type="checkbox" checked={draft?.italic === true} onChange={(event) => update("italic", event.target.checked || undefined)} /></label>
      <label>Underline<input type="checkbox" checked={draft?.underline === true} onChange={(event) => update("underline", event.target.checked || undefined)} /></label>
      <label>Text color<input type="color" value={draft?.textColor ?? "#000000"} onChange={(event) => update("textColor", event.target.value)} onBlur={() => { window.clearTimeout(persistTimeout.current); commit(writeStyle(pendingStyle.current)); }} /></label>
      <label>Background color<input type="color" value={draft?.backgroundColor ?? "#ffffff"} onChange={(event) => update("backgroundColor", event.target.value)} onBlur={() => { window.clearTimeout(persistTimeout.current); commit(writeStyle(pendingStyle.current)); }} /></label>
      {(["padding", "marginTop", "marginBottom"] as const).map((key) => <label key={key}>{key}<input type="number" min="0" max="144" value={draft?.[key] ?? ""} onChange={(event) => update(key, Number.isFinite(event.target.valueAsNumber) ? Math.min(144, Math.max(0, event.target.valueAsNumber)) : undefined)} /></label>)}
      <label>Alignment<select value={draft?.textAlign ?? ""} onChange={(event) => update("textAlign", (event.target.value || undefined) as BlockStyle["textAlign"])}><option value="">Document default</option><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></label>
    </div>}
  </section>;
};

export const StyleInspector = (props: { value?: BlockStyle | string; onChange: (style?: string) => void }) => {
  const selectedId = useBuilderPuck((state) => state.selectedItem?.props?.id);
  return <StyleFields key={selectedId ?? ""} {...props} />;
};
